import React, { useMemo, type JSX } from 'react'
import { Controller } from 'react-hook-form'
import { ExperienceSearchParams } from '@reward-platform/ancillaries-schemas/experience'
import { createAutoCompleteLocationSource } from '~/utils/createAutoCompleteSourceFromLocation'
import {
  AutoCompleteLocationOption,
  AutoCompleteWithMobileModal,
  DatePicker,
} from '@reward-platform/lift/components'
import { useQueryParams } from '~/hooks/useQueryParams/useQueryParams'
import { useLocalisation } from '~/context/localisation'
import { Button } from '@alto-avios/alto-ui'
import { useExperienceQueryData } from '~/hooks/useExperiencePageData/useExperienceQueryData'
import { useTranslations } from 'next-intl'
import { useTagManagerContext } from '~/context/analytics'
import { useSearchPageContent } from '~/hooks/useSearchPage'
import { SearchTabQuerySchema } from '~/schemas/searchPageParams'
import { useQueryStateToFormState } from '../ExperienceResults/useQueryToFormState'
import {
  useDateRangeState,
  useDestinationState,
  useExperienceSearchForm,
} from './useExperienceSearch'
import styles from './SearchExperienceForm.module.scss'

type SearchExperienceFormProps = {
  buttonText: string
  initialSearch?: boolean
  contentfulEntryId?: string
  contentfulRevisionId?: number
}

const SearchExperienceForm = React.forwardRef<
  {
    press: () => void
  },
  SearchExperienceFormProps
>((props: SearchExperienceFormProps, toggleRef): JSX.Element => {
  const { buttonText, initialSearch = true, contentfulEntryId, contentfulRevisionId } = props
  const t = useTranslations()
  const { query } = useQueryParams<ExperienceSearchParams>()
  const { locale } = useLocalisation()
  const defaultValues = useQueryStateToFormState(query)

  const fieldName: keyof ExperienceSearchParams = 'destination'
  const { pushEventToDataLayer } = useTagManagerContext()
  const { form, errors, handleFormSubmit } = useExperienceSearchForm(defaultValues, toggleRef)
  const destination = useDestinationState(form, defaultValues.destination)
  const { fromDate, toDate } = useExperienceQueryData()

  // Resolve Contentful IDs: props take precedence (passed from parent when available),
  // otherwise fall back to the hook (self-contained for pages that don't pass IDs)
  const { getTabContentByProductSlug } = useSearchPageContent()
  const experiencesContent = getTabContentByProductSlug(SearchTabQuerySchema.enum.experiences)
  const effectiveEntryId = contentfulEntryId ?? experiencesContent?.entryId
  const effectiveRevisionId = contentfulRevisionId ?? experiencesContent?.revisionId
  const autocompleteItems = useMemo(
    () => createAutoCompleteLocationSource(destination.locations),
    [destination.locations]
  )
  const dates = useDateRangeState(form, [defaultValues.startDateTime, defaultValues.endDateTime])
  const { control, handleSubmit } = form

  const trackExperienceSearchDefaultObject = {
    nav_type: 'cta',
    nav_action: 'search again',
    from_date: fromDate,
    to_date: toDate,
    destination: destination.fullName,
    ...(effectiveEntryId !== undefined && { contentful_entry_id: effectiveEntryId }),
    ...(effectiveRevisionId !== undefined && { contentful_revision_id: effectiveRevisionId }),
  }

  const trackExperienceSearchButton = () => {
    if (initialSearch) {
      trackExperienceSearchDefaultObject.nav_action = 'search_experiences'
    }
    pushEventToDataLayer('navigation', trackExperienceSearchDefaultObject)
  }

  return (
    <form className={styles.container} autoComplete="off" onSubmit={handleSubmit(handleFormSubmit)}>
      <Controller
        control={control}
        name={fieldName}
        render={() => (
          <div>
            <AutoCompleteWithMobileModal
              gridArea="var(--search-experience-location)"
              name={fieldName}
              label={t('common.forms.labels.destination')}
              value={destination.searchString}
              items={autocompleteItems}
              onInputFocus={destination.onInputFocus}
              onInputValueChange={(val) => {
                return destination.handleAutocompleteChange(val.inputValue ?? '')
              }}
              onSelectedItemChange={(value) => {
                destination.handleSuggestionClick(value.selectedItem?.value || '')
              }}
              error={errors.getDestinationError()}
            >
              {
                // eslint-disable-next-line no-nested-ternary
                destination?.isLoading ? (
                  <div className={styles.destination}>
                    {t('pages.search.experiences.actions.search_button')}
                  </div>
                ) : destination.noResultsFound ? (
                  <div className={styles.destination}>
                    {t('common.forms.labels.no_results')} <b>"{destination.searchString}"</b>
                  </div>
                ) : (
                  autocompleteItems.map((item, index) => {
                    return <AutoCompleteLocationOption key={item.key} item={item} index={index} />
                  })
                )
              }
            </AutoCompleteWithMobileModal>
          </div>
        )}
      />
      <div className={styles.rangeDatePicker}>
        <DatePicker
          isRangePicker
          startLabel={t('pages.search.experiences.labels.from')}
          endLabel={t('pages.search.experiences.labels.to')}
          onChange={(newDates) => {
            if (Array.isArray(newDates)) {
              const [startDate, endDate] = newDates
              dates.handleDateChange([startDate || dates.startDate, endDate || null])
            }
          }}
          values={{ startDateTime: dates.startDate, endDateTime: dates.endDate || undefined }}
          minDate={dates.minDate}
          maxDate={dates.maxDate}
          endDateSelectable
          locale={locale}
          errorStartDate={errors.getFieldError('startDateTime')}
          errorEndDate={errors.getFieldError('endDateTime')}
        />
      </div>

      <Button type="submit" onPress={() => trackExperienceSearchButton()}>
        {buttonText}
      </Button>
    </form>
  )
})

export default SearchExperienceForm
